import "./Dashboard.css";

import { ArrowheadRightDarkIcon } from "../../../assets";
import { OrganisationTypeConstants } from "../../../constants";
import { AssetManagerTemplate } from "../../../templates/AssetManagerTemplate";
import { WorkflowTaskPanel } from "../../../templates/WorkflowTaskPanel";
import { YourTasksPanel } from "../../../templates/YourTasksPanel";
import { getPipelineTrackerRoute } from "../../../utils/routes";
import { Card, Column, CursorDataGrid, Row, TogglePieChartKPI } from "../../../widget";
import { ProjectInvitationBanner } from "../../shared/components";
import { Banner, DashboardTableCard } from "./components";
import { useDashboard } from "./useDashboard";
import { useYourTasksWidget } from "./useYourTasksWidget";

const Dashboard = (): JSX.Element => {
  const {
    refresh,
    isLoading,
    isLoadingLatestDeals,
    bannerUrl,
    firstName,
    dealStageChartData,
    dealTypeChartData,
    dealTypeOrStage,
    fundDetails,
    tableColumnsLatestDeals,
    pipelinesData,
    selectedPipeline,
    defaultSortingCriteria,
    setDealTypeOrStage,
    onLatestDealsDataLoad,
    handleChangePipelineFilter,
  } = useDashboard();

  const {
    columnsTasks,
    tasksDataLoad,
    isYourTasksPanelOpen,
    setIsYourTasksPanelOpen,
    refreshTasks,
    handleRefresh,
    selectedPanelTask,
    setSelectedPanelTask,
    showWorkflowTaskPanel,
    setShowWorkflowTaskPanel,
    handleCloseWorkflowTaskPanel,
    workflow,
  } = useYourTasksWidget();

  return (
    <AssetManagerTemplate activeElement="Dashboard">
      <div>
        <Banner displayedBanner={bannerUrl} firstName={firstName} fundName={fundDetails.name} />
        <Row spacingV="ml" style={{ paddingTop: 272 }}>
          <Column span={12}>
            <div className="DashboardHeader">
              <h1>Dashboard</h1>
            </div>
          </Column>
        </Row>
        <ProjectInvitationBanner />
        {!isLoading && (
          <Row spacingV="l">
            <Column span={6}>
              <Card height="100%" corners="rounded" padding="ml">
                <div className="PortfolioContainer">
                  <p className="body01">Portfolio</p>
                  <div className="KPIs">
                    <div>
                      {/* TODO - populate from getPortfolio when we build that api */}
                      <h1>{fundDetails.currentSize}</h1>
                      <p className="body2Light"> Current size</p>
                    </div>
                    <div className="Divider" />
                    <div>
                      <h1>{fundDetails.targetSize}</h1>
                      <p className="body2Light"> Target size</p>
                    </div>
                    <div className="Divider" />
                    <div>
                      <h1>{fundDetails.targetIrr}</h1>
                      <p className="body2Light"> Target IRR</p>
                    </div>
                  </div>
                </div>
              </Card>
            </Column>
            <Column span={6}>
              <TogglePieChartKPI
                data={dealTypeOrStage === "By type" ? dealTypeChartData : dealStageChartData}
                title={`Deals ${dealTypeOrStage.toLowerCase()}`}
                toggleTabsList={[{ label: "By type" }, { label: "By stage" }]}
                currentTab={dealTypeOrStage}
                onChange={(value) => setDealTypeOrStage(value)}
              />
            </Column>
          </Row>
        )}
        <Row spacingV="ml">
          <Column span={12}>
            <DashboardTableCard
              title="Latest deals"
              expandRoute={getPipelineTrackerRoute(OrganisationTypeConstants.ASSET_MANAGER)}
              onChange={onLatestDealsDataLoad}
              columns={tableColumnsLatestDeals}
              isLoading={isLoadingLatestDeals}
              selectedPipeline={selectedPipeline}
              onChangePipelines={handleChangePipelineFilter}
              pipelinesData={pipelinesData}
              refresh={refresh}
              defaultSortingCriteria={defaultSortingCriteria}
            />
          </Column>
        </Row>
        <div className="YourTasksWidget">
          <div className="YourTasksHeader">
            <h2>Your tasks</h2>
            <button className="YourTasksViewAllBtn" onClick={() => setIsYourTasksPanelOpen(true)} type="button">
              View all
              <ArrowheadRightDarkIcon />
            </button>
          </div>
          <CursorDataGrid
            data={[]}
            columns={columnsTasks}
            clientSide={false}
            onChange={tasksDataLoad}
            dataIsLoading={isLoading}
            noDataMessage="No tasks to display"
            filterable
            showFilterToggle
            pageable={false}
            showTotalCount={false}
            hasBottomMargin={false}
            sortable={false}
            refresh={refreshTasks}
            hasNoPadding={false}
            variant="seed"
          />
        </div>
        <YourTasksPanel
          show={isYourTasksPanelOpen}
          onClose={() => setIsYourTasksPanelOpen(false)}
          setSelectedPanelTask={setSelectedPanelTask}
          setShowWorkflowTaskPanel={setShowWorkflowTaskPanel}
          refreshTasks={refreshTasks}
        />
        <WorkflowTaskPanel
          task={selectedPanelTask}
          currentStepUuid={selectedPanelTask?.step?.uuid}
          workflow={workflow}
          show={showWorkflowTaskPanel}
          refresh={refreshTasks}
          handleRefresh={handleRefresh}
          onClose={handleCloseWorkflowTaskPanel}
        />
      </div>
    </AssetManagerTemplate>
  );
};

export default Dashboard;
